import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Trash2, Pencil, ExternalLink } from 'lucide-react';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import RequireAuth from '../components/auth/RequireAuth';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import SEO from '../components/seo/SEO';
import { useLanguage } from '../context/LanguageContext';
import {
  getReaderComments,
  updateReaderComment,
  deleteReaderComment,
  resendVerificationEmail,
} from '../services/authService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 3rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0 0 0.35rem;
  font-size: 2rem;
  font-weight: 700;
`;

const PageSub = styled.p`
  margin: 0 0 2rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ProfileCard = styled.div`
  padding: 1.25rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.card};
`;

const ProfileGrid = styled.dl`
  display: grid;
  gap: 0.75rem;
  margin: 0;
  font-size: 0.9375rem;

  dt {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.mutedForeground};
  }

  dd {
    margin: 0;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.125rem;
  font-weight: 600;
`;

const CommentCard = styled.div`
  padding: 1rem 1.15rem;
  margin-bottom: 0.75rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.card};
`;

const CommentMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const StatusBadge = styled.span`
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${({ $status, theme }) => {
    if ($status === 'approved') return `${theme.colors.primary}22`;
    if ($status === 'pending') return '#f59e0b22';
    if ($status === 'spam') return '#dc262622';
    return `${theme.colors.mutedForeground}22`;
  }};
  color: ${({ $status, theme }) => {
    if ($status === 'approved') return theme.colors.primary;
    if ($status === 'pending') return '#b45309';
    if ($status === 'spam') return theme.colors.destructive || '#dc2626';
    return theme.colors.mutedForeground;
  }};
`;

const ArticleLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CommentBody = styled.div`
  font-size: 0.9375rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.foreground};

  a {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 0.65rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-family: inherit;
  font-size: 0.9375rem;
  resize: vertical;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.6rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: transparent;
  font-size: 0.8125rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.foreground};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &.danger:hover {
    border-color: ${({ theme }) => theme.colors.destructive || '#dc2626'};
    color: ${({ theme }) => theme.colors.destructive || '#dc2626'};
  }
`;

const EmptyText = styled.p`
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: 0.9375rem;
`;

const VerifyBanner = styled.div`
  padding: 1rem 1.15rem;
  margin-bottom: 1.5rem;
  border-radius: 10px;
  border: 1px solid #f59e0b55;
  background: #f59e0b12;
  font-size: 0.9375rem;
  line-height: 1.55;

  p {
    margin: 0 0 0.75rem;
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function AccountContent() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useSelector((s) => s.auth);
  const { getArticleUrl } = useLanguage();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  const justRegistered = Boolean(location.state?.justRegistered);
  const registerNotice = justRegistered
    ? location.state?.registerMessage ||
      (location.state?.emailSent === false
        ? t('auth.registerCheckEmailFailed')
        : t('auth.registerCheckEmail'))
    : '';

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getReaderComments({ limit: 50 });
      setComments(res.data || []);
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const startEdit = (comment) => {
    setEditingId(comment._id);
    const plain = String(comment.body || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    setEditBody(plain);
  };

  const saveEdit = async (commentId) => {
    setActionLoading(commentId);
    try {
      await updateReaderComment(commentId, editBody.trim());
      setEditingId(null);
      await loadComments();
    } finally {
      setActionLoading(null);
    }
  };

  const removeComment = async (commentId) => {
    if (!window.confirm(t('account.deleteConfirm'))) return;
    setActionLoading(commentId);
    try {
      await deleteReaderComment(commentId);
      await loadComments();
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMsg('');
    try {
      const res = await resendVerificationEmail();
      setResendMsg(res.message || t('auth.verifyResent'));
    } catch (err) {
      setResendMsg(err?.response?.data?.message || t('auth.verifyResendFailed'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <PageWrapper>
      <SEO title={t('account.title')} description={t('account.sub')} robots="noindex, follow" />
      <Header />
      <Main>
        <Container>
          <PageTitle>{t('account.title')}</PageTitle>
          <PageSub>{t('account.sub')}</PageSub>

          {!user?.emailVerified ? (
            <VerifyBanner>
              <p>{registerNotice || t('auth.verifyBanner')}</p>
              <Button size="sm" type="button" onClick={handleResendVerification} disabled={resendLoading}>
                {resendLoading ? t('auth.verifyResending') : t('auth.verifyResend')}
              </Button>
              {resendMsg ? <p style={{ marginTop: '0.75rem', marginBottom: 0 }}>{resendMsg}</p> : null}
            </VerifyBanner>
          ) : null}

          <ProfileCard>
            <ProfileGrid>
              <div>
                <dt>{t('auth.displayNameLabel')}</dt>
                <dd>{user?.username}</dd>
              </div>
              <div>
                <dt>{t('auth.emailLabel')}</dt>
                <dd>
                  {user?.email}
                  {user?.emailVerified ? ` (${t('auth.verified')})` : ` (${t('auth.unverified')})`}
                </dd>
              </div>
            </ProfileGrid>
            <div style={{ marginTop: '1rem' }}>
              <Link to="/bookmarks" style={{ textDecoration: 'none' }}>
                <Button variant="secondary" size="sm" type="button">
                  {t('account.viewBookmarks')}
                </Button>
              </Link>
            </div>
          </ProfileCard>

          <SectionTitle>{t('account.myComments')}</SectionTitle>

          {loading ? (
            <EmptyText>{t('account.loadingComments')}</EmptyText>
          ) : comments.length === 0 ? (
            <EmptyText>{t('account.noComments')}</EmptyText>
          ) : (
            comments.map((comment) => {
              const slug = comment.articleSlug;
              const articleUrl = slug ? getArticleUrl(slug) : null;
              const isEditing = editingId === comment._id;
              const canModify = comment.status === 'pending';

              return (
                <CommentCard key={comment._id}>
                  <CommentMeta>
                    <StatusBadge $status={comment.status}>{comment.status}</StatusBadge>
                    <span>{formatDate(comment.createdAt)}</span>
                    {articleUrl && slug ? (
                      <ArticleLink to={articleUrl}>
                        {comment.articleTitle || slug}
                        <ExternalLink size={12} />
                      </ArticleLink>
                    ) : null}
                  </CommentMeta>

                  {isEditing ? (
                    <>
                      <EditTextarea
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        maxLength={2000}
                      />
                      <Actions>
                        <Button
                          size="sm"
                          onClick={() => saveEdit(comment._id)}
                          disabled={actionLoading === comment._id}
                        >
                          {t('account.saveComment')}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>
                          {t('account.cancelEdit')}
                        </Button>
                      </Actions>
                    </>
                  ) : (
                    <>
                      <CommentBody dangerouslySetInnerHTML={{ __html: comment.body }} />
                      {canModify ? (
                        <Actions>
                          <IconBtn type="button" onClick={() => startEdit(comment)}>
                            <Pencil size={14} />
                            {t('account.editComment')}
                          </IconBtn>
                          <IconBtn
                            type="button"
                            className="danger"
                            onClick={() => removeComment(comment._id)}
                            disabled={actionLoading === comment._id}
                          >
                            <Trash2 size={14} />
                            {t('account.deleteComment')}
                          </IconBtn>
                        </Actions>
                      ) : null}
                    </>
                  )}
                </CommentCard>
              );
            })
          )}
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
}

export default function Account() {
  return (
    <RequireAuth>
      <AccountContent />
    </RequireAuth>
  );
}
